/*!
 *  (c)(r) 2008-2023 IT-Service Robert Frunzke
 */

import { arr_inArray, array_unique, str_trim, mj_deep_extend } from './util.js';
import { startProgress, stopProgress } from './progress-util.js';
import { jQuery } from '../other/jquery-hooked.js';
import { postToBackend, postToBackend2 } from './fetch-util.js';
import { MPlugins } from './plugins.js';


export let MDMedia = function($) {

	var m_meta_changed_observers = [];							// [ function( media_ids, meta ), ... ]
	var m_categories_changed_observers = [];					// [ function( media_ids, categories ), ... ]
	var m_media_deleted_observers = [];							// [ function( media_ids ), ... ]
	var m_media_restored_observers = [];						// [ function( media_ids ), ... ]

	var m_media_dtinfo_cache = {};								// { <media_id>: { timestamp:number, meta:object, categories:object, r_download:bool, r_edit:bool, r_delete:bool }, ... }
	var m_dtinfo_cache_max_age_ms = 5*60*1000;					// max. age of dtinfo cache entries in ms

	var m_media_preview_availability_cache = {};				// { media_id:dt, ... }
	var m_preview_availability_cache_max_age_ms = 5*60*1000;	// max. age of entries in m_media_preview_availability_cache

	var m_recently_deleted_media_ids = [];

	function invalidateMediaCaches( media_ids ) {
		const ml = media_ids.length;
		for( var i=0; i<ml; ++i ) {
			const mid = media_ids[i];
			if( m_media_dtinfo_cache.hasOwnProperty(mid) ) delete m_media_dtinfo_cache[mid];
			if( m_media_preview_availability_cache.hasOwnProperty(mid) ) delete m_media_preview_availability_cache[mid];
		}
	}

	function invalidateAllMediaCaches() {
		m_media_dtinfo_cache = {};
		m_media_preview_availability_cache = {};
	}

	function queryMediaPreviewAvailable( media_id, success_cb, error_cb, prioritize ) {
		if( m_media_preview_availability_cache.hasOwnProperty(media_id) ) {
			if( Date.now() - m_media_preview_availability_cache[media_id] < m_preview_availability_cache_max_age_ms ) {
				// use cached result
				return success_cb(media_id,true);
			}
			// cache entry is out-of-date: re-query
			delete m_media_preview_availability_cache[media_id];
		}
		var postdata = 'media_id='+encodeURIComponent(media_id);
		if( prioritize ) postdata += '&prioritize=1';
		return postToBackend2('json/media/meta-dtpreviewavailable',postdata,function( j ) {
			var avl = j && j.available;
			if( avl ) m_media_preview_availability_cache[media_id] = Date.now();
			success_cb(media_id,avl);
		}, function() {
			// timeout
			if( error_cb ) error_cb(media_id,'timeout');
		}, function( XMLHttpRequest, textStatus ) {
			// other error
			if( error_cb ) error_cb(media_id,XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
		} );
	}

	function addToMediaPreviewAvailabilityCache( media_availability_status ) {
		//console.log(['addToMediaPreviewAvailabilityCache',media_availability_status]);
		var now = Date.now();
		for( var media_id in media_availability_status ) {
			if( media_availability_status.hasOwnProperty(media_id) ) {
				if( media_availability_status[media_id] ) m_media_preview_availability_cache[media_id] = now;
				else if( m_media_preview_availability_cache.hasOwnProperty(media_id) ) delete m_media_preview_availability_cache[media_id];
			}
		}
	}


	function queryMediaDetailInfo( media_id, success_cb, error_cb, silent ) {
		return queryMediaDetailInfo2(
			[ media_id ],
			function( dtinfos ) {
				success_cb(dtinfos[media_id]);
			},
			error_cb,
			silent
		);
	}

	function queryMediaDetailInfo2( media_ids, success_cb, error_cb, silent ) {
		var dtinfo_for_success_cb = {};
		var query_media_ids = [];
		for( var i=0; i<media_ids.length; ++i ) {
			var media_id = media_ids[i];
			if( m_media_dtinfo_cache.hasOwnProperty(media_id) && Date.now() - m_media_dtinfo_cache[media_id].timestamp < m_dtinfo_cache_max_age_ms ) {
				// use cached result
				dtinfo_for_success_cb[media_id] = m_media_dtinfo_cache[media_id];
			} else {
				// cache entry is out-of-date: re-query
				delete m_media_dtinfo_cache[media_id];
				query_media_ids.push(media_id);
			}
		}
		// query
		if( query_media_ids.length===0 ) {
			// nothing left to query ...
			return success_cb(dtinfo_for_success_cb);
		}
		silent = !!silent;
		if( !silent ) startProgress();
		return postToBackend2( 'json/media/meta-dtinfo2', 'media_ids='+encodeURIComponent(query_media_ids.join(',')), function(j) {
			if( j && j.hasOwnProperty('results') ) {
				$.each( j.results, function( idx, result ) {
					if( result.hasOwnProperty('meta') ) {
						var media_id = idx;
						if( !result.meta ) result.meta = { '-1': [0] };
						// success
						const dtinfo = {
							media_id:			media_id,
							media_class:		result.meta[-1] ? parseInt(result.meta[-1][0],10) : 0,
							version:			result.meta[13] && result.meta[13][0] ? parseInt(result.meta[13][0],10) : null,
							is_latest_version:	true,
							meta:				result.meta,
							categories:			result.categories || {},
							r_download:			!!result.r_download,
							r_edit:				!!result.r_edit,
							r_delete:			!!result.r_delete,
							timestamp:			Date.now(),
						};
						dtinfo.r_annots_view = !!result.r_annots_view;
						dtinfo.r_annots_edit = !!result.r_annots_edit;
						dtinfo.r_annots_admin = !!result.r_annots_admin;
						m_media_dtinfo_cache[media_id] = dtinfo;
						dtinfo_for_success_cb[media_id] = dtinfo;
					}
				} );
				success_cb(dtinfo_for_success_cb);
				if( !silent ) stopProgress();
			} else {
				// error
				if( !silent ) stopProgress();
				if( error_cb ) error_cb(j&&j.error?j.error:'unknown error');
			}
		}, function() {
			// timeout
			if( !silent ) stopProgress();
			if( error_cb ) error_cb('timeout');
		}, function( XMLHttpRequest, textStatus ) {
			// other error
			if( !silent ) stopProgress();
			if( error_cb && (XMLHttpRequest.statusText??null)!=='abort' ) error_cb(XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
		} );
	}

	function queryMediaDetailVersionInfo( media_id, version, success_cb, error_cb, silent, update_dtinfo_cache_of_latest_version ) {
		if( version===null ) {
			if( update_dtinfo_cache_of_latest_version ) {
				invalidateMediaCaches([media_id]);
			}
			return queryMediaDetailInfo(media_id,success_cb,error_cb,silent);
		}
		// query
		silent = !!silent;
		if( !silent ) startProgress();
		return postToBackend2( 'json/media/version-info', 'media_id='+encodeURIComponent(media_id)+'&version='+encodeURIComponent(version), function(j) {
			if( j && j.is_latest_version ) {
				if( update_dtinfo_cache_of_latest_version ) {
					invalidateMediaCaches([media_id]);
				}
				// re-query using "full detail" query for latest version... (additionally this supports the dtinfo cache)
				var result = queryMediaDetailInfo(media_id,success_cb,error_cb,true);
				if( !silent ) stopProgress();
				return result;
			}
			if( j && j.hasOwnProperty('meta') ) {
				if( !j.meta ) j.meta = { '-1': [0] };
				// success
				const dtinfo = {
					media_id:			media_id,
					media_class:		j.meta[-1] ? parseInt(j.meta[-1][0],10) : 0,
					version:			j.meta[13] && j.meta[13][0] ? parseInt(j.meta[13][0],10) : (j.is_latest_version?null:'unknown'),
					is_latest_version:	!!j.is_latest_version,
					meta:				j.meta,
					categories:			j.categories || {},
					r_download:			!!j.r_download,
					r_edit:				!!j.r_edit,
					r_delete:			!!j.r_delete,
					timestamp:			Date.now(),
				};
				dtinfo.r_annots_view = !!j.r_annots_view;
				dtinfo.r_annots_edit = !!j.r_annots_edit;
				dtinfo.r_annots_admin = !!j.r_annots_admin;
				if( success_cb ) success_cb(dtinfo);
				if( !silent ) stopProgress();
			} else {
				//console.log(['media/version-info', j ]);
				// error
				if( !silent ) stopProgress();
				if( error_cb ) error_cb(j&&j.error?j.error:'unknown error');
			}
		}, function() {
			// timeout
			if( !silent ) stopProgress();
			if( error_cb ) error_cb('timeout');
		}, function( XMLHttpRequest, textStatus ) {
			// other error
			if( !silent ) stopProgress();
			if( error_cb && (XMLHttpRequest.statusText??null)!=='abort' ) error_cb(XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
		} );
	}

	function queryMediaEditableCount( media_ids, success_cb ) {
		var ml=media_ids.length, ec=0, mid;
		// check cached values (dtinfo-cache) ...
		for( var i=0; i<ml; ++i ) {
			mid = media_ids[i];
			if( m_media_dtinfo_cache.hasOwnProperty(mid) ) {
				// found in cache.. count
				if( m_media_dtinfo_cache[mid].r_edit ) ec++;
			} else {
				// not in cache -> must query (and to keep it simple, just query for all..)
				return postToBackend('json/media/meta-editable_count', 'media_ids='+encodeURIComponent(media_ids.join(',')), function(j) {
					success_cb( j.r_editable_count );
				} );
			}
		}
		// found everything in cache ...
		return success_cb( ec );
	}

	function triggerMetaChanged( media_ids, data, trigger_meta_ids, trigger_tree_ids, success_cb, skip_cache_update ) {
		// console.log({w:'triggerMetaChanged',media_ids, data, trigger_meta_ids, trigger_tree_ids, success_cb, skip_cache_update});
		var has_meta=data.hasOwnProperty('meta'),
			has_categories=data.hasOwnProperty('categories'),
			ml=media_ids.length;
		var i, omid;
		if( !skip_cache_update ) {
			// update dtinfo cache
			for( i=0; i<ml; ++i ) {
				omid = media_ids[i];
				if( m_media_dtinfo_cache.hasOwnProperty(omid) ) {
					// NOTE: important to check whether data for media_id really is available (it may not be the case...)
					if( has_meta && data.meta.hasOwnProperty(omid) ) m_media_dtinfo_cache[omid].meta = data.meta[omid] || {'-1':[0]};
					if( has_categories && data.categories.hasOwnProperty(omid) ) m_media_dtinfo_cache[omid].categories = data.categories[omid] || {};
				}
			}
		}
		// determine actually updated media_ids (in contrast to those who were requested to be updated)
		var actually_meta_changed_ids = [],
			actually_categories_changed_ids = [],
			actually_changed_ids = [];
		for( i=0; i<ml; ++i ) {
			omid = media_ids[i];
			var hm = has_meta && data.meta.hasOwnProperty(omid),
				hc = has_categories && data.categories.hasOwnProperty(omid);
			if( hm ) actually_meta_changed_ids.push(omid);
			if( hc ) actually_categories_changed_ids.push(omid);
			if( hm || hc ) actually_changed_ids.push(omid);
		}
		data.actually_meta_changed_ids = actually_meta_changed_ids;
		data.actually_categories_changed_ids = actually_categories_changed_ids;
		data.actually_changed_ids = actually_changed_ids;
		// call all observers
		if( success_cb ) {
			success_cb(data,actually_changed_ids);
		}
		if( has_meta ) {
			//console.log([m_meta_changed_observers.length,'triggerMetaChanged [has_meta] (actually_meta_changed_ids,data.meta,trigger_meta_ids)',actually_meta_changed_ids,data.meta,trigger_meta_ids]);
			$.each( m_meta_changed_observers, function(idx,fn) {
				// console.log(['fn(actually_meta_changed_ids,data.meta,trigger_meta_ids)',actually_meta_changed_ids,data.meta,trigger_meta_ids]);
				fn(actually_meta_changed_ids,data.meta,trigger_meta_ids);
			} );
		}
		if( has_categories ) {
			//console.log([m_categories_changed_observers.length,'triggerMetaChanged [has_cats] (actually_categories_changed_ids,data.categories,trigger_tree_ids)',actually_categories_changed_ids,data.categories,trigger_tree_ids]);
			$.each( m_categories_changed_observers, function(idx,fn) {
				// console.log(['fn(actually_categories_changed_ids,data.categories,trigger_tree_ids)',actually_categories_changed_ids,data.categories,trigger_tree_ids]);
				fn(actually_categories_changed_ids,data.categories,trigger_tree_ids);
			} );
		}
	}

	function triggerFullMetaChanged( media_ids, data, do_trigger_meta, do_trigger_trees, success_cb, silent  ) {
		var _stage2 = function( media_ids, data, success_cb, skip_cache_update ) {
			var trigger_meta_ids={}, trigger_tree_ids={}, ml=media_ids.length;
			for( var i=0; i<ml; ++i ) {
				var media_id = media_ids[i];
				if( do_trigger_meta ) {
					$.each( data.meta[media_id], function(meta_id,value) {
						trigger_meta_ids[meta_id] = meta_id;
					} );
				}
				if( do_trigger_trees ) {
					$.each( data.categories[media_id], function(tree_id,cat_ids) {
						trigger_tree_ids[tree_id] = tree_id;
					} );
				}
			}
			//console.log( ['triggerFullMetaChanged -> done:',media_ids,data,trigger_meta_ids,trigger_tree_ids,skip_cache_update] );
			MDMedia.triggerMetaChanged(media_ids,data,trigger_meta_ids,trigger_tree_ids,success_cb,skip_cache_update);
		};
		if( data===null ) {
			// query actual data
			var count_down = media_ids.length;
			data = {};
			if( do_trigger_meta ) data.meta = {};
			if( do_trigger_trees ) data.categories = {};
			if( !silent ) startProgress();
			$.each( media_ids, function(i,media_id) {
				queryMediaDetailInfo(
					media_id,
					function( dtinfo ) {
						// success_cb
						if( do_trigger_meta ) data.meta[media_id] = dtinfo.meta;
						if( do_trigger_trees ) data.categories[media_id] = dtinfo.categories;
						if( --count_down <= 0 ) {
							_stage2(media_ids,data,success_cb,true);
							if( !silent ) stopProgress();
						}
					},
					function( error ) {
						// error_cb
						if( --count_down <= 0 ) {
							_stage2(media_ids,data,success_cb,true);
							if( !silent ) stopProgress();
						}
					},
					true
				);
			} );
		} else {
			// use provided data
			_stage2(media_ids,data,success_cb,false);
			if( !silent ) stopProgress();
		}
	}

	function saveMetaData( media_ids, data, success_cb, error_cb, silent ) {
		if( !Array.isArray(media_ids) ) media_ids = [media_ids];
		silent = !!silent;
		if( !silent ) startProgress();

		// map of meta_id to MediaMetaSaveData plugin instances
		var save_data_plugins = MPlugins.getByTypeMappedByMetaId('MediaMetaSaveData','beforeSaveMetaData'),
			ml = media_ids.length;

		function stage_2( cur_dtinfos ) {
			var postdata = 'media_ids='+encodeURIComponent(media_ids.join(','));
			var trigger_meta_ids = {};
			var trigger_tree_ids = {};
			if( data.hasOwnProperty('meta') ) {
				if( Object.size(save_data_plugins) ) {
					// first pass on data - call the plugins
					//  - plugins may add/remove or change meta data values of other fields
					$.each( data.meta, function(meta_id,val_arr) {
						if( save_data_plugins.hasOwnProperty(meta_id) ) {
							var plugin = save_data_plugins[meta_id];
							for( var i=0; i<ml; ++i ) {
								var media_id = media_ids[i],
									cur_data = cur_dtinfos[media_id] || {};
								plugin.beforeSaveMetaData( media_id, meta_id, data, cur_data );
							}
						}
					} );
				}
				// second pass on data - prepare postdata and track actually affected meta_ids
				//  - including the changes from plugins
				$.each( data.meta, function(meta_id,val_arr) {
					trigger_meta_ids[meta_id] = meta_id;
					if( !Array.isArray(val_arr) ) val_arr = [val_arr];
					var vl = val_arr.length;
					for( var i=0; i<vl; ++i ) postdata += '&meta_'+meta_id+'_'+i+'='+encodeURIComponent(str_trim(val_arr[i].toString()));
				} );
				postdata += '&get_meta=1';
			}
			if( data.hasOwnProperty('categories') ) {
				$.each( data.categories, function(tree_id,cat_ids) {
					trigger_tree_ids[tree_id] = tree_id;
					postdata += '&cat_'+encodeURIComponent(tree_id)+'='+encodeURIComponent( Array.isArray(cat_ids) ? cat_ids.join(',') : cat_ids );
				} );
				postdata += '&get_categories=1';
				// NOTE: on category change, some meta data will change too (version-*, modified-*, ..)
				// so, retrieve those changes too:
				postdata += '&get_meta=1';
			}
			return postToBackend2('json/media/manage-update', postdata, function(j) {
				if( j && !j.error ) {
					// ok, update cache & call observers
					//console.log( ['saveMetaData -> done:',media_ids,data,j,trigger_meta_ids,trigger_tree_ids] );
					triggerMetaChanged(media_ids,j,trigger_meta_ids,trigger_tree_ids,success_cb);
					if( !silent ) stopProgress();
				} else {
					// error
					if( !silent ) stopProgress();
					if( error_cb ) error_cb(j.error);
				}
			}, function() {
				// timeout
				if( !silent ) stopProgress();
				if( error_cb ) error_cb('timeout');
			}, function( XMLHttpRequest, textStatus ) {
				// other error
				if( !silent ) stopProgress();
				if( error_cb && (XMLHttpRequest.statusText??null)!=='abort' ) error_cb(XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
			} );
		}

		if( data.hasOwnProperty('meta') && Object.size(save_data_plugins) ) {
			// get current meta data  ->  beforeSaveMetaData() may require access to it ...
			return queryMediaDetailInfo2( media_ids, function( dtinfos ) {
				const cloned_dtinfos = mj_deep_extend({},dtinfos);
				stage_2(cloned_dtinfos);
			}, error_cb, true );
		}
		return stage_2(null);

	}

	function saveMetaValue( media_ids, meta_id, value, success_cb, error_cb, silent ) {
		var data = { meta:{} };
		data.meta[meta_id] = value;
		return saveMetaData( media_ids, data, function( updated_data ) {
			if( success_cb ) success_cb(updated_data.meta);
		}, error_cb, silent );
	}


	function triggerMetaValueListPlugins( meta_id, metadata_update_list, cur_dtinfos ) {
		var changes_by_media_id = {};
		var additional_meta_data_from_plugins = [];
		MPlugins.iterateByTypeAndMetaId('MediaMetaSaveData',meta_id,function(plugin_name,plugin) {
			// first pass on data - call the plugins
			//  - plugin may add/remove or change meta data values of other fields
			for( var i=0; i<metadata_update_list.length; ++i ) {
				var mdu = metadata_update_list[i],
					media_id = mdu[0],
					tmp_meta = {};
				tmp_meta[meta_id] = Array.isArray(mdu[1]) ? mdu[1].slice(0) : [mdu[1]];
				var tmp_data = { 'meta': tmp_meta };
				var cur_data = cur_dtinfos[media_id] || {};
				plugin.beforeSaveMetaData( media_id, meta_id, tmp_data, cur_data );
				$.each( tmp_data.meta, function(aux_meta_id,aux_val_arr) {
					aux_meta_id = parseInt(aux_meta_id,10);
					if( !changes_by_media_id.hasOwnProperty(media_id) ) changes_by_media_id[media_id] = {};
					if( !changes_by_media_id[media_id].hasOwnProperty(aux_meta_id) ) changes_by_media_id[media_id][aux_meta_id] = {};
					changes_by_media_id[media_id][aux_meta_id] = aux_val_arr;
					if( aux_meta_id==meta_id ) {
						// plugin changed value of actual meta_id field
						metadata_update_list[i][1] = aux_val_arr;
					} else {
						// plugin added value of some other meta_id field
						additional_meta_data_from_plugins.push( [media_id,aux_meta_id,aux_val_arr] );
					}
				} );
			}
		});
		return [changes_by_media_id,metadata_update_list,additional_meta_data_from_plugins];
	}


	function saveMetaValueList( meta_id, metadata_update_list, success_cb, error_cb, silent ) {
		silent = !!silent;
		if( !silent ) startProgress();
		// unique list of media_ids ...
		var media_ids = [];
		for( var i=0; i<metadata_update_list.length; ++i ) media_ids.push( metadata_update_list[i][0] );
		media_ids = array_unique(media_ids);

		function stage_2( cur_dtinfos ) {
			var postdata = '';
			var trigger_meta_ids = {};
			var trigger_tree_ids = {};

			var rtmp = triggerMetaValueListPlugins(meta_id,metadata_update_list,cur_dtinfos);
			metadata_update_list = rtmp[1];
			var additional_meta_data_from_plugins = rtmp[2];
			var i, vl, k;

			// prepare postdata
			for( i=0; i<metadata_update_list.length; ++i ) {
				var mdu = metadata_update_list[i],
					media_id = mdu[0],
					val_arr = Array.isArray(mdu[1]) ? mdu[1] : [mdu[1]];
				trigger_meta_ids[meta_id] = meta_id;
				vl = val_arr.length;
				for( k=0; k<vl; ++k ) postdata += '&meta_'+media_id+'_'+meta_id+'_'+k+'='+encodeURIComponent(str_trim(val_arr[k].toString()));
			}
			for( i=0; i<additional_meta_data_from_plugins.length; ++i ) {
				var amdfp = additional_meta_data_from_plugins[i],
					aux_meta_id = amdfp[1],
					aux_val_arr = amdfp[2];
				trigger_meta_ids[aux_meta_id] = aux_meta_id;
				vl = aux_val_arr.length;
				for( k=0; k<vl; ++k ) postdata += '&meta_'+amdfp[0]+'_'+aux_meta_id+'_'+k+'='+encodeURIComponent(str_trim(aux_val_arr[k].toString()));
			}
			postdata += '&get_meta=1';
			return postToBackend2('json/media/manage-update2', postdata, function(j) {
				if( j && !j.error ) {
					// ok, update cache & call observers
					//console.log( ['saveMetaValueList -> done:',media_ids,metadata_update_list,j,trigger_meta_ids,trigger_tree_ids] );
					triggerMetaChanged(media_ids,j,trigger_meta_ids,trigger_tree_ids,success_cb);
					if( !silent ) stopProgress();
				} else {
					// error
					if( !silent ) stopProgress();
					if( error_cb ) error_cb(j.error);
				}
			}, function() {
				// timeout
				if( !silent ) stopProgress();
				if( error_cb ) error_cb('timeout');
			}, function( XMLHttpRequest, textStatus ) {
				// other error
				if( !silent ) stopProgress();
				if( error_cb && (XMLHttpRequest.statusText??null)!=='abort' ) error_cb(XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
			} );
		}

		if( MPlugins.hasPluginOfTypeForMetaId('MediaMetaSaveData',meta_id) ) {
			// get current meta data  ->  beforeSaveMetaData() may require access to it ...
			return queryMediaDetailInfo2( media_ids, function( dtinfos ) {
				const cloned_dtinfos = mj_deep_extend({},dtinfos);
				stage_2(cloned_dtinfos);
			}, error_cb, true );
		}
		return stage_2(null);

	}

	function addMetaValue( media_ids, meta_id, value, success_cb, error_cb, silent ) {
		if( !Array.isArray(media_ids) ) media_ids = [media_ids];
		silent = !!silent;
		if( !silent ) startProgress();
		var postdata = 'media_ids='+encodeURIComponent(media_ids.join(','));
		var trigger_meta_ids = {};
		trigger_meta_ids[meta_id] = meta_id;
		if( !Array.isArray(value) ) value = [value];
		var vl = value.length;
		for( var i=0; i<vl; ++i ) postdata += '&meta_'+meta_id+'_'+i+'='+encodeURIComponent(str_trim(value[i].toString()));
		postdata += '&get_meta=1';
		return postToBackend2('json/media/manage-addmetavalues', postdata, function(j) {
			if( j && !j.error ) {
				// ok, update cache & call observers
				//console.log( ['addMetaValue -> done:',media_ids,meta_id,value,j,trigger_meta_ids] );
				triggerMetaChanged(media_ids,j,trigger_meta_ids,{},function(updated_data) {
					if( success_cb ) success_cb(updated_data.meta);
				});
				if( !silent ) stopProgress();
			} else {
				// error
				if( !silent ) stopProgress();
				if( error_cb ) error_cb(j.error);
			}
		}, function() {
			// timeout
			if( !silent ) stopProgress();
			if( error_cb ) error_cb('timeout');
		}, function( XMLHttpRequest, textStatus ) {
			// other error
			if( !silent ) stopProgress();
			if( error_cb && (XMLHttpRequest.statusText??null)!=='abort' ) error_cb(XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
		} );
	}

	function saveCategoryAssignment( media_ids, tree_id, cat_ids, success_cb, error_cb, silent, get_meta ) {
		var data = { categories:{} };
		data.categories[tree_id] = Array.isArray(cat_ids) ? cat_ids : [cat_ids];
		if( get_meta ) data.meta = {};
		return saveMetaData( media_ids, data, function( updated_data ) {
			if( success_cb ) {
				if( get_meta ) success_cb(updated_data.categories,updated_data.meta,updated_data);
				else success_cb(updated_data.categories,updated_data);
			}
		}, error_cb, silent );
	}

	function addCategoryAssignment( media_ids, tree_id, cat_ids, success_cb, error_cb, silent ) {
		if( !Array.isArray(media_ids) ) media_ids = [media_ids];
		silent = !!silent;
		if( !silent ) startProgress();
		var postdata = 'media_ids='+encodeURIComponent(media_ids.join(','));
		var trigger_tree_ids = {};
		trigger_tree_ids[tree_id] = tree_id;
		postdata += '&cat_'+encodeURIComponent(tree_id)+'='+encodeURIComponent( Array.isArray(cat_ids) ? cat_ids.join(',') : cat_ids );
		postdata += '&get_categories=1';
		return postToBackend2('json/media/manage-addcatassignments', postdata, function(j) {
			if( j && !j.error ) {
				// ok, update cache & call observers
				//console.log( ['addCategoryAssignment -> done:',media_ids,tree_id,cat_ids,j,trigger_tree_ids] );
				triggerMetaChanged(media_ids,j,{},trigger_tree_ids,function(updated_data) {
					if( success_cb ) success_cb(updated_data.categories,updated_data);
				});
				if( !silent ) stopProgress();
			} else {
				// error
				if( !silent ) stopProgress();
				if( error_cb ) error_cb(j.error);
			}
		}, function() {
			// timeout
			if( !silent ) stopProgress();
			if( error_cb ) error_cb('timeout');
		}, function( XMLHttpRequest, textStatus ) {
			// other error
			if( !silent ) stopProgress();
			if( error_cb && (XMLHttpRequest.statusText??null)!=='abort' ) error_cb(XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
		} );
	}

	function deleteMedia( media_ids, success_cb, error_cb, silent ) {
		if( !Array.isArray(media_ids) ) media_ids = [media_ids];
		silent = !!silent;
		if( !silent ) startProgress();
		return postToBackend2( 'json/media/manage-delete', 'media_ids='+encodeURIComponent(media_ids.join(',')), function(j) {
			if( j && !j.error && j.hasOwnProperty('media_ids') ) {
				// update dtinfo cache & list of "recently deleted media ids"
				const ml = j.media_ids.length;
				for( var i=0; i<ml; ++i ) {
					const omid = j.media_ids[i];
					if( m_media_dtinfo_cache.hasOwnProperty(omid) ) delete m_media_dtinfo_cache[omid];
					if( m_media_preview_availability_cache.hasOwnProperty(omid) ) delete m_media_preview_availability_cache[omid];
					m_recently_deleted_media_ids.push(omid);
				}
				// success
				if( success_cb ) success_cb(j.media_ids,j);
				// call observers
				$.each( m_media_deleted_observers, function(idx,fn) {
					fn(j.media_ids);
				} );
				if( !silent ) stopProgress();
			} else {
				// error
				if( !silent ) stopProgress();
				if( error_cb ) error_cb(j.error);
			}
		}, function() {
			// timeout
			if( !silent ) stopProgress();
			if( error_cb ) error_cb('timeout');
		}, function( XMLHttpRequest, textStatus ) {
			// other error
			if( !silent ) stopProgress();
			if( error_cb && (XMLHttpRequest.statusText??null)!=='abort' ) error_cb(XMLHttpRequest.responseJSON && XMLHttpRequest.responseJSON.error ? XMLHttpRequest.responseJSON.error : XMLHttpRequest.status);
		} );
	}

	// TODO: move the UNDO call out of snackbar, and refactor this: ...
	//  - this should be a fn "restoreMedia()" - similar to "deleteMedia()" ...
	function onAfterRestoreMedia( media_ids, success_cb ) {

		// invalidate caches ...
		invalidateMediaCaches(media_ids);

		// update m_recently_deleted_media_ids ...
		const ml = media_ids.length;
		for( var i=0; i<ml; ++i ) {
			const mid = media_ids[i];
			const idx = arr_inArray(mid,m_recently_deleted_media_ids);
			if( idx != -1 ) {
				m_recently_deleted_media_ids.splice(idx,1);
			}
		}

		const trigger_meta = true;
		const trigger_trees = true;
		triggerFullMetaChanged( media_ids, null, trigger_meta, trigger_trees, function(tmp_data,tmp_actually_changed_ids) {
			if( success_cb ) {
				success_cb(media_ids);
			}
			// call "restored" observers
			$.each( m_media_restored_observers, function(idx,fn) {
				// tmp_data.meta, tmp_data.categories;
				fn(media_ids,tmp_data);
			} );
			// NOTE: m_meta_changed_observers and m_categories_changed_observers  will be called now ...
		} );
	}

	function wasRecentlyDeleted( media_id ) {
		return arr_inArray(media_id,m_recently_deleted_media_ids) != -1;
	}

	return {
		bindMetaChanged: function( fn ) {
			m_meta_changed_observers.push(fn);
		},
		bindCategoriesChanged: function( fn ) {
			m_categories_changed_observers.push(fn);
		},
		bindMediaDeleted: function( fn ) {
			m_media_deleted_observers.push(fn);
		},
		bindMediaRestored: function( fn ) {
			m_media_restored_observers.push(fn);
		},

		queryMediaDetailInfo: queryMediaDetailInfo,
		queryMediaDetailInfo2: queryMediaDetailInfo2,
		queryMediaDetailVersionInfo: queryMediaDetailVersionInfo,
		queryMediaEditableCount: queryMediaEditableCount,

		triggerMetaChanged: triggerMetaChanged,
		triggerFullMetaChanged: triggerFullMetaChanged,
		invalidateMediaCaches: invalidateMediaCaches,
		invalidateAllMediaCaches: invalidateAllMediaCaches,

		queryMediaPreviewAvailable: queryMediaPreviewAvailable,
		addToMediaPreviewAvailabilityCache: addToMediaPreviewAvailabilityCache,

		saveMetaData: saveMetaData,

		saveMetaValue: saveMetaValue,
		addMetaValue: addMetaValue,

		triggerMetaValueListPlugins: triggerMetaValueListPlugins,
		saveMetaValueList: saveMetaValueList,

		saveCategoryAssignment: saveCategoryAssignment,
		addCategoryAssignment: addCategoryAssignment,

		deleteMedia,
		onAfterRestoreMedia,

		wasRecentlyDeleted,
	};
}(jQuery); // namespace MDMedia
